import type { Locale } from '../i18n';
import { currentLocale, loadLocale, onLocaleChange, t } from '../i18n';
import {
  GPU_ENABLED_STORAGE_KEY,
  readInitialGpuEnabled,
  type DemoState,
  type StatePatch,
} from '../state';
import { isIpadLike } from '../util/ipad-detection';
import { createGpuDiagnosticIndicator } from './gpu-diagnostic-indicator';
import type { GpuDiagnosticsInput } from '@amitone/dither-core';

// Theme type mirrors main.ts - kept local to avoid a circular import.
export type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'amitone-theme';
const FOCUSABLE_POPUP_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]';

export function readSavedTheme(): Theme {
  const v = localStorage.getItem(THEME_KEY);
  if (v === 'light') return 'light';
  if (v === 'dark') return 'dark';
  return 'system';
}

export function applyTheme(theme: Theme): void {
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.dataset['theme'] = prefersDark ? 'dark' : 'light';
  } else {
    document.documentElement.dataset['theme'] = theme;
  }
  localStorage.setItem(THEME_KEY, theme);
}

export interface SettingsPopupHandles {
  /** Tear down all event listeners and remove from the DOM. */
  destroy(): void;
}

export type ClearPwaCacheResult = 'cleared' | 'cancelled' | 'unavailable' | 'failed';
type PwaCacheUnavailableReason =
  | 'insecure-origin'
  | 'missing-cache-storage'
  | 'missing-service-worker'
  | 'missing-service-worker-list-api';

interface PwaCacheCapability {
  available: boolean;
  reason?: PwaCacheUnavailableReason;
}

export interface ClearPwaCacheOptions {
  reload?: () => void;
  confirm?: (message: string) => boolean;
  message?: string;
  currentUrl?: string;
}

function cacheNameMatchesScope(cacheName: string, scope: string): boolean {
  return cacheName.startsWith('workbox-') && cacheName.endsWith(scope);
}

function serviceWorkerCanListRegistrations(serviceWorker: ServiceWorkerContainer): boolean {
  return (
    typeof serviceWorker.getRegistration === 'function' ||
    typeof serviceWorker.getRegistrations === 'function'
  );
}

function getPwaCacheCapability(): PwaCacheCapability {
  if (globalThis.isSecureContext === false) {
    return { available: false, reason: 'insecure-origin' };
  }

  const cacheStorage = globalThis.caches;
  if (cacheStorage === undefined) {
    return { available: false, reason: 'missing-cache-storage' };
  }

  const serviceWorker = navigator.serviceWorker;
  if (serviceWorker === undefined) {
    return { available: false, reason: 'missing-service-worker' };
  }

  if (!serviceWorkerCanListRegistrations(serviceWorker)) {
    return { available: false, reason: 'missing-service-worker-list-api' };
  }

  return { available: true };
}

function cacheUnavailableStatusKey(
  reason: PwaCacheUnavailableReason | undefined,
  iPadLike: boolean,
): string {
  if (reason !== 'insecure-origin') {
    return 'settings.cache.unavailable';
  }

  return iPadLike
    ? 'settings.cache.unavailable.insecureOrigin.ipad'
    : 'settings.cache.unavailable.insecureOrigin.mac';
}

async function findCurrentServiceWorkerRegistration(
  serviceWorker: ServiceWorkerContainer,
  currentUrl: string,
): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof serviceWorker.getRegistration === 'function') {
    return serviceWorker.getRegistration(currentUrl);
  }

  if (typeof serviceWorker.getRegistrations !== 'function') {
    return undefined;
  }

  const registrations = await serviceWorker.getRegistrations();
  return registrations
    .filter((registration) => currentUrl.startsWith(registration.scope))
    .sort((a, b) => b.scope.length - a.scope.length)[0];
}

export async function clearPwaCacheAndReload(
  options: ClearPwaCacheOptions = {},
): Promise<ClearPwaCacheResult> {
  const capability = getPwaCacheCapability();
  if (!capability.available) {
    return 'unavailable';
  }

  const cacheStorage = globalThis.caches;
  const serviceWorker = navigator.serviceWorker;
  if (
    cacheStorage === undefined ||
    serviceWorker === undefined ||
    !serviceWorkerCanListRegistrations(serviceWorker)
  ) {
    return 'unavailable';
  }

  const message = options.message ?? t('settings.cache.confirm');
  const confirm = options.confirm ?? ((confirmMessage: string) => window.confirm(confirmMessage));
  if (!confirm(message)) {
    return 'cancelled';
  }

  try {
    const currentUrl = options.currentUrl ?? window.location.href;
    const registration = await findCurrentServiceWorkerRegistration(serviceWorker, currentUrl);
    const appScope = registration?.scope ?? new URL('./', currentUrl).href;
    if (registration !== undefined) {
      await registration.unregister();
    }

    const cacheNames = await cacheStorage.keys();
    for (const cacheName of cacheNames) {
      if (cacheNameMatchesScope(cacheName, appScope)) {
        await cacheStorage.delete(cacheName);
      }
    }

    (options.reload ?? (() => window.location.reload()))();
    return 'cleared';
  } catch {
    return 'failed';
  }
}

export interface SettingsPopupGpuOptions {
  getGpuState?: () => Pick<
    DemoState,
    'gpuEnabled' | 'gpuEnabledForSession' | 'lastGpuDiagnostics'
  >;
  patchGpuState?: (patch: Pick<StatePatch, 'gpuEnabled' | 'gpuEnabledForSession'>) => void;
  webgl2Available?: () => boolean;
  reinitialiseGpu?: () => void;
  reload?: () => void;
  isIpadLike?: () => boolean;
  /** Optional accessor returning the full runtime GPU diagnostics input.
   * When provided, the diagnostics popup displays real hardware data. When
   * absent (renderer not yet available or WebGL2 unavailable), a safe
   * "no-renderer" fallback is shown instead. */
  getGpuDiagnosticsInput?: () => GpuDiagnosticsInput;
}

function defaultWebgl2Available(): boolean {
  try {
    return document.createElement('canvas').getContext('webgl2') !== null;
  } catch {
    return false;
  }
}

function fallbackGpuState(): Pick<
  DemoState,
  'gpuEnabled' | 'gpuEnabledForSession' | 'lastGpuDiagnostics'
> {
  return {
    gpuEnabled: readInitialGpuEnabled(),
    gpuEnabledForSession: false,
    lastGpuDiagnostics: null,
  };
}

function persistFallbackGpuEnabled(enabled: boolean): void {
  localStorage.setItem(GPU_ENABLED_STORAGE_KEY, String(enabled));
}

/**
 * Creates a settings gear button in `parent` and manages a popup that opens
 * below-right of the button when clicked.
 *
 * Popup contents:
 *   - Theme: Light / Dark / System segmented control.
 *   - Language: EN / JA segmented control.
 *
 * Closes on outside click, Escape key, or clicking the gear again.
 * Focus is trapped inside the popup while it is open.
 */
export function createSettingsPopup(
  parent: HTMLElement,
  gpuOptions: SettingsPopupGpuOptions = {},
): SettingsPopupHandles {
  // ── Gear button ──────────────────────────────────────────────────────────
  const gearButton = document.createElement('button');
  gearButton.type = 'button';
  // Use settings-trigger (no outer box frame) rather than top-bar-btn (Item 5).
  gearButton.className = 'settings-trigger settings-gear-btn';
  gearButton.setAttribute('aria-haspopup', 'dialog');
  gearButton.setAttribute('aria-expanded', 'false');
  gearButton.setAttribute('aria-label', t('aria.settings.gear'));
  // SVG gear icon - CSS classes control sizing.
  gearButton.innerHTML =
    '<svg class="top-bar-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
    '<path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 0 1-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 0 1 .947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 0 1 2.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 0 1 2.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 0 1 .947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 0 1-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 0 1-2.287-.947zM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" clip-rule="evenodd" />' +
    '</svg>';
  parent.appendChild(gearButton);

  // ── Popup ──────────────────────────────────────────────────────────────────
  const popup = document.createElement('div');
  popup.className = 'settings-popup settings-gear-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-modal', 'true');
  popup.hidden = true;
  // Keep the fixed popup out of the horizontally scrolling top bar. iPad
  // Safari can still clip fixed descendants inside overflow scrollers.
  document.body.appendChild(popup);

  // ── Theme section ──────────────────────────────────────────────────────────
  const themeSection = document.createElement('div');
  themeSection.className = 'settings-popup-section';
  popup.appendChild(themeSection);

  const themeLabelEl = document.createElement('div');
  themeLabelEl.className = 'settings-popup-label';
  themeLabelEl.dataset['i18n'] = 'settings.theme.label';
  themeLabelEl.textContent = t('settings.theme.label');
  themeSection.appendChild(themeLabelEl);

  const themeControl = document.createElement('div');
  themeControl.className = 'segmented-control';
  themeControl.setAttribute('role', 'group');
  themeSection.appendChild(themeControl);

  const makeSegBtn = (
    key: string,
    value: string,
    group: HTMLElement,
  ): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'segmented-control-btn';
    btn.dataset['value'] = value;
    btn.dataset['i18n'] = key;
    btn.textContent = t(key);
    group.appendChild(btn);
    return btn;
  };

  const themeLightBtn = makeSegBtn('settings.theme.light', 'light', themeControl);
  const themeDarkBtn = makeSegBtn('settings.theme.dark', 'dark', themeControl);
  const themeSystemBtn = makeSegBtn('settings.theme.system', 'system', themeControl);

  let currentTheme: Theme = readSavedTheme();

  function syncThemeButtons(): void {
    for (const btn of [themeLightBtn, themeDarkBtn, themeSystemBtn]) {
      btn.setAttribute('aria-pressed', String(btn.dataset['value'] === currentTheme));
    }
    themeLabelEl.textContent = t('settings.theme.label');
  }
  syncThemeButtons();

  for (const btn of [themeLightBtn, themeDarkBtn, themeSystemBtn]) {
    btn.addEventListener('click', () => {
      currentTheme = btn.dataset['value'] as Theme;
      applyTheme(currentTheme);
      syncThemeButtons();
    });
  }

  // Listen for OS theme changes when 'system' is selected.
  const systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = (): void => {
    if (currentTheme === 'system') {
      applyTheme('system');
    }
  };
  systemThemeQuery.addEventListener('change', handleSystemThemeChange);

  // ── Language section ────────────────────────────────────────────────────────
  const langSection = document.createElement('div');
  langSection.className = 'settings-popup-section';
  popup.appendChild(langSection);

  const langLabelEl = document.createElement('div');
  langLabelEl.className = 'settings-popup-label';
  langLabelEl.dataset['i18n'] = 'settings.language.label';
  langLabelEl.textContent = t('settings.language.label');
  langSection.appendChild(langLabelEl);

  const langControl = document.createElement('div');
  langControl.className = 'segmented-control';
  langControl.setAttribute('role', 'group');
  langSection.appendChild(langControl);

  const langEnBtn = makeSegBtn('settings.language.en', 'en', langControl);
  const langJaBtn = makeSegBtn('settings.language.ja', 'ja', langControl);

  function syncLangButtons(): void {
    const locale = currentLocale();
    for (const btn of [langEnBtn, langJaBtn]) {
      btn.setAttribute('aria-pressed', String(btn.dataset['value'] === locale));
    }
    langLabelEl.textContent = t('settings.language.label');
    themeLabelEl.textContent = t('settings.theme.label');
    syncThemeButtons();
    // Refresh button text for all segmented controls after locale swap.
    for (const btn of [themeLightBtn, themeDarkBtn, themeSystemBtn, langEnBtn, langJaBtn]) {
      const key = btn.dataset['i18n'];
      if (key) btn.textContent = t(key);
    }
    gearButton.setAttribute('aria-label', t('aria.settings.gear'));
  }

  syncLangButtons();

  for (const btn of [langEnBtn, langJaBtn]) {
    btn.addEventListener('click', () => {
      void loadLocale(btn.dataset['value'] as Locale);
    });
  }

  onLocaleChange(syncLangButtons);

  // ── Storage section ────────────────────────────────────────────────────────
  const storageSection = document.createElement('div');
  storageSection.className = 'settings-popup-section';

  const storageLabelEl = document.createElement('div');
  storageLabelEl.className = 'settings-popup-label';
  storageLabelEl.dataset['i18n'] = 'settings.storage.label';
  storageLabelEl.textContent = t('settings.storage.label');
  storageSection.appendChild(storageLabelEl);

  const clearCacheButton = document.createElement('button');
  clearCacheButton.type = 'button';
  clearCacheButton.className = 'btn btn-ghost';
  clearCacheButton.dataset['i18n'] = 'settings.cache.clear';
  clearCacheButton.textContent = t('settings.cache.clear');
  storageSection.appendChild(clearCacheButton);

  const cacheStatusEl = document.createElement('div');
  cacheStatusEl.className = 'row-hint';
  cacheStatusEl.setAttribute('role', 'status');
  cacheStatusEl.setAttribute('aria-live', 'polite');
  cacheStatusEl.hidden = true;
  storageSection.appendChild(cacheStatusEl);

  let cacheStatusKey: string | undefined;
  let cacheClearInFlight = false;

  function cacheStatusDeviceIsIpadLike(): boolean {
    return (gpuOptions.isIpadLike ?? isIpadLike)();
  }

  function setCacheStatus(key: string | undefined): void {
    cacheStatusKey = key;
    cacheStatusEl.textContent = key ? t(key) : '';
    cacheStatusEl.hidden = key === undefined;
  }

  function syncStorageSection(): void {
    storageLabelEl.textContent = t('settings.storage.label');
    clearCacheButton.textContent = t('settings.cache.clear');
    const capability = getPwaCacheCapability();
    clearCacheButton.disabled = !capability.available || cacheClearInFlight;
    if (!capability.available) {
      setCacheStatus(cacheUnavailableStatusKey(capability.reason, cacheStatusDeviceIsIpadLike()));
      return;
    }
    if (cacheStatusKey !== undefined) {
      cacheStatusEl.textContent = t(cacheStatusKey);
    }
  }

  syncStorageSection();

  clearCacheButton.addEventListener('click', () => {
    if (cacheClearInFlight) return;
    if (!getPwaCacheCapability().available) {
      syncStorageSection();
      return;
    }

    cacheClearInFlight = true;
    setCacheStatus('settings.cache.clearing');
    syncStorageSection();
    void clearPwaCacheAndReload({ message: t('settings.cache.confirm') }).then((result) => {
      cacheClearInFlight = false;
      if (result === 'cleared') return;
      if (result === 'failed') {
        setCacheStatus('settings.cache.failed');
      } else if (result === 'unavailable') {
        setCacheStatus(
          cacheUnavailableStatusKey(
            getPwaCacheCapability().reason,
            cacheStatusDeviceIsIpadLike(),
          ),
        );
      } else {
        setCacheStatus(undefined);
      }
      syncStorageSection();
    });
  });

  onLocaleChange(syncStorageSection);

  // ── GPU section ────────────────────────────────────────────────────────────
  const gpuSection = document.createElement('div');
  gpuSection.className = 'settings-popup-section';
  popup.appendChild(gpuSection);

  const gpuToggleRow = document.createElement('div');
  gpuToggleRow.className = 'row row-checkboxes';
  gpuSection.appendChild(gpuToggleRow);

  const gpuToggleCluster = document.createElement('span');
  gpuToggleCluster.className = 'checkbox-cluster';
  gpuToggleRow.appendChild(gpuToggleCluster);

  const gpuToggleInput = document.createElement('input');
  gpuToggleInput.type = 'checkbox';
  gpuToggleInput.id = 'settings-gpu-acceleration';
  gpuToggleInput.dataset['gpuToggle'] = 'true';
  gpuToggleCluster.appendChild(gpuToggleInput);

  const gpuToggleLabel = document.createElement('label');
  gpuToggleLabel.htmlFor = gpuToggleInput.id;
  gpuToggleLabel.dataset['i18n'] = 'settings.gpu.label';
  gpuToggleLabel.textContent = t('settings.gpu.label');
  gpuToggleCluster.appendChild(gpuToggleLabel);

  const readGpuState = (): Pick<
    DemoState,
    'gpuEnabled' | 'gpuEnabledForSession' | 'lastGpuDiagnostics'
  > =>
    gpuOptions.getGpuState?.() ?? fallbackGpuState();

  const patchGpuState = (patch: Pick<StatePatch, 'gpuEnabled' | 'gpuEnabledForSession'>): void => {
    if (gpuOptions.patchGpuState) {
      gpuOptions.patchGpuState(patch);
      return;
    }
    if (patch.gpuEnabled !== undefined) {
      persistFallbackGpuEnabled(patch.gpuEnabled);
    }
  };

  function syncGpuToggle(): void {
    const enabled = readGpuState().gpuEnabled;
    const available = (gpuOptions.webgl2Available ?? defaultWebgl2Available)();
    gpuToggleLabel.textContent = t('settings.gpu.label');
    gpuToggleInput.checked = enabled;
    gpuToggleInput.disabled = !available;
    if (available) {
      gpuToggleInput.removeAttribute('title');
      gpuToggleLabel.removeAttribute('title');
    } else {
      const disabledTooltip = t('settings.gpu.tooltip.disabled');
      gpuToggleInput.title = disabledTooltip;
      gpuToggleLabel.title = disabledTooltip;
    }
  }

  gpuToggleInput.addEventListener('change', () => {
    const current = readGpuState().gpuEnabled;
    const next = !current;
    const iPadLike = (gpuOptions.isIpadLike ?? isIpadLike)();
    if (iPadLike && next && !window.confirm(t('settings.gpu.reloadConfirm'))) {
      syncGpuToggle();
      syncGpuDiagnosticIndicator();
      return;
    }

    patchGpuState({ gpuEnabled: next });
    syncGpuToggle();
    syncGpuDiagnosticIndicator();

    if (iPadLike) {
      if (next) {
        (gpuOptions.reload ?? (() => location.reload()))();
      }
      return;
    }
    gpuOptions.reinitialiseGpu?.();
  });

  syncGpuToggle();

  const gpuDiagnosticOptions = {
    getState: readGpuState,
    ...(gpuOptions.reinitialiseGpu ? { reinitialiseGpu: gpuOptions.reinitialiseGpu } : {}),
    ...(gpuOptions.isIpadLike ? { isIpadLike: gpuOptions.isIpadLike } : {}),
    ...(gpuOptions.getGpuDiagnosticsInput
      ? { getGpuDiagnosticsInput: gpuOptions.getGpuDiagnosticsInput }
      : {}),
  };
  const gpuDiagnosticIndicator = createGpuDiagnosticIndicator(gpuDiagnosticOptions);
  popup.appendChild(gpuDiagnosticIndicator);
  popup.appendChild(storageSection);

  function syncGpuDiagnosticIndicator(): void {
    // Show the sticky indicator only when the user has GPU turned ON in
    // preferences but the session has been disabled (e.g., iPad context
    // loss locked the session, or the desktop self-test failed after a
    // restore). The default state is both flags false (GPU off, session
    // never enabled), and the indicator must stay hidden in that case so
    // it does not appear by default for every user who opens settings.
    const s = readGpuState();
    const iPadLike = (gpuOptions.isIpadLike ?? isIpadLike)();
    const canRetryOnDesktop = !iPadLike && gpuOptions.reinitialiseGpu !== undefined;
    const hasPauseDiagnostics = s.lastGpuDiagnostics !== null;
    gpuDiagnosticIndicator.hidden = !(
      s.gpuEnabled &&
      !s.gpuEnabledForSession &&
      hasPauseDiagnostics &&
      (iPadLike || canRetryOnDesktop)
    );
  }
  syncGpuDiagnosticIndicator();

  onLocaleChange(() => {
    syncGpuToggle();
    syncGpuDiagnosticIndicator();
  });

  // ── Open / Close ────────────────────────────────────────────────────────────
  let isOpen = false;

  function positionPopup(): void {
    const margin = 8;
    const offset = 5;
    const triggerRect = gearButton.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const topbarRect = parent.closest<HTMLElement>('.top-bar')?.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const width = popupRect.width || 240;
    const height = popupRect.height || 120;
    const anchorBottom = Math.max(
      triggerRect.bottom,
      parentRect.bottom,
      topbarRect?.bottom ?? 0,
    );
    const belowTop = anchorBottom + offset;
    const aboveTop = triggerRect.top - height - offset;
    const canFitBelow = belowTop + height <= window.innerHeight - margin;
    const unclampedTop = canFitBelow || aboveTop < margin ? belowTop : aboveTop;
    const left = Math.max(
      margin,
      Math.min(triggerRect.right - width, window.innerWidth - width - margin),
    );
    const top = Math.max(
      margin,
      Math.min(unclampedTop, window.innerHeight - height - margin),
    );

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
    popup.style.right = 'auto';
  }

  function openPopup(): void {
    isOpen = true;
    popup.hidden = false;
    positionPopup();
    gearButton.setAttribute('aria-expanded', 'true');
    // Focus the first focusable element in the popup.
    const first = popup.querySelector<HTMLElement>(FOCUSABLE_POPUP_SELECTOR);
    first?.focus();
  }

  function closePopup(): void {
    if (!isOpen) return;
    isOpen = false;
    popup.hidden = true;
    gearButton.setAttribute('aria-expanded', 'false');
    gearButton.focus();
  }

  gearButton.addEventListener('click', () => {
    if (isOpen) {
      closePopup();
    } else {
      openPopup();
    }
  });

  // ── Outside-click close ─────────────────────────────────────────────────────
  const handleDocumentClick = (event: MouseEvent): void => {
    if (!isOpen) return;
    if (
      !popup.contains(event.target as Node) &&
      event.target !== gearButton &&
      !gearButton.contains(event.target as Node)
    ) {
      closePopup();
    }
  };
  document.addEventListener('click', handleDocumentClick, true);

  // ── Escape key ──────────────────────────────────────────────────────────────
  const handleKeydown = (event: KeyboardEvent): void => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      closePopup();
      return;
    }
    // Focus trap: Tab / Shift+Tab cycles within the popup.
    if (event.key === 'Tab') {
      const focusable = Array.from(
        popup.querySelectorAll<HTMLElement>(FOCUSABLE_POPUP_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  };
  document.addEventListener('keydown', handleKeydown, true);

  const handleViewportChange = (): void => {
    if (isOpen) positionPopup();
  };
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);

  // ── Cleanup ──────────────────────────────────────────────────────────────────
  return {
    destroy(): void {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('keydown', handleKeydown, true);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      systemThemeQuery.removeEventListener('change', handleSystemThemeChange);
      gearButton.remove();
      popup.remove();
    },
  };
}
